/**
 * Customer 360's header facts, its scheduled-change banner, its Overview cards and the Subscription & calendar side
 * column (D19 phase 21b, after the owner's Customer360 board).
 *
 * The board's structure, our data. Every figure comes from a read this page already makes (customerReads.ts); where
 * the board shows something nothing in the ERP computes yet — the rate a bill will use, the month's estimate, the next
 * bill run, the route packet version — its place holds a PlannedPanel with the v6 step that brings it. A read that is
 * refused (the viewer lacks its permission) leaves its fact out rather than showing a zero.
 */
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarCheck, CalendarClock, CircleAlert, CircleCheck, MapPin, Pencil, Phone, Route as RouteIcon, Truck, User } from 'lucide-react';
import { useSession } from '../../lib/session';
import { cutoffWords, useBusinessDay } from '../../lib/businessDay';
import { Button, StatusBadge, Unknown, cx } from '../../ui/primitives';
import { PlannedPanel } from '../../ui/PlannedPanel';
import { ScrapNote } from '../../ui/ScrapNote';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { VariantBottle } from '../../ui/VariantBottle';
import { CustomerCalendar } from './CustomerCalendar';
import { OverviewCard, useOpenTab } from './CustomerViews';
import {
  addDays, dayLines, inr, istToday, linesLabel, monthStart, nextMonth, shortDay, unitsOf,
  balanceSourceLine, ledgerCheckPath, outstandingSplit, useCalendarMonth, useCalendarRange, useComplaints, useCustomerBalance, useCustomerTimeline, useDeliveryFacts, useMonthBill,
  type CalendarDay, heldShort, usePhoneDays, waitingUnits, type PhoneDay } from './customerReads';
import type { Customer360 } from './customerDescriptor';
import { dateTime as fmtDateTime } from '../../ui/format';
import { ArrangementManage, MoveDoorButton } from './CustomerManage';

/** Opens the Change drawer (its state lives in the URL, `?change=`, so the page and every button share it). */
export function useOpenChange() {
  const [params, setParams] = useSearchParams();
  return (type?: string) => {
    const copy = new URLSearchParams(params);
    copy.set('change', type ?? '1');
    setParams(copy, { replace: true });
  };
}

export function ChangeDeliveryButton({ type, label = 'Change delivery', variant = 'primary' }: { type?: string; label?: string; variant?: 'primary' | 'secondary' }) {
  const open = useOpenChange();
  return (
    <Button variant={variant} size="dense" onClick={() => open(type)}>
      <Pencil size={13} aria-hidden />{label}
    </Button>
  );
}

const months = (since: string) => {
  const a = new Date(`${since.slice(0, 10)}T00:00:00Z`), b = new Date(`${istToday()}T00:00:00Z`);
  const m = (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + b.getUTCMonth() - a.getUTCMonth();
  return m < 1 ? 'this month' : m < 12 ? `${m} mo` : `${Math.floor(m / 12)} yr ${m % 12} mo`;
};

/** The days of this month so far, from the calendar read: what was handed over, changed, skipped and failed. */
function monthSoFar(days: readonly CalendarDay[], today: string) {
  const past = days.filter((d) => d.date <= today);
  const given = past.reduce((n, d) => n + (d.delivered?.given ?? 0), 0);
  const deliveredDays = past.filter((d) => (d.delivered?.given ?? 0) > 0).length;
  const notDelivered = past.filter((d) => d.delivered?.outcome === 'NOT_DELIVERED').length;
  const partial = past.filter((d) => d.delivered?.outcome === 'PARTIAL').length;
  const changed = past.filter((d) => d.changes.length > 0 || d.extraOrders > 0).length;
  const comingPlanned = days.filter((d) => d.date > today && unitsOf(dayLines(d)) > 0).length;
  return { given, deliveredDays, notDelivered, partial, changed, comingPlanned };
}

/* ── Header: identity line and the summary strip, in the board's order ─────────────────────────────── */

export function CustomerHeaderFacts({ entity }: { entity: Customer360 }) {
  const today = istToday();
  const facts = useDeliveryFacts(entity.id);
  const balance = useCustomerBalance(entity.id);
  const { can } = useSession();
  const cal = useCalendarMonth(entity.id, monthStart(today), can('subscription.view'));
  const complaints = useComplaints(entity.id);
  const phone = usePhoneDays(entity.id);
  const door = entity.locations.find((l) => l.is_primary) ?? entity.locations[0];
  const f = facts.data;

  // Each figure opens the tab that holds its whole story (the owner, 2026-10-07: "the rupees KPI clickable").
  const openTab = useOpenTab();
  const cells: { label: string; value: string; sub?: string; tone?: string; open?: [string, string] }[] = [];
  if (balance.data !== undefined && !balance.error) {
    const b = balance.data;
    cells.push({ label: 'Outstanding', value: inr(b?.balance_paise), tone: (b?.balance_paise ?? 0) > 0 ? 'text-refused' : undefined,
      sub: b ? `${outstandingSplit(b)} · ${balanceSourceLine(b.source)}${b.last_paid_on ? ` · last paid ${shortDay(b.last_paid_on)}` : ' · never paid'}` : 'nothing handed over yet', open: ['money', 'dues'] });
  }
  if (cal.data && !cal.error) {
    const s = monthSoFar(cal.data.days, cal.data.today);
    // The rider's hand-overs the ERP has not booked yet (held: no stock on the round) — said, not added to the booked figure.
    const waiting = cal.data.days.filter((d) => d.date <= cal.data!.today).reduce((n, d) => n + waitingUnits(phone.data?.get(d.date), d.delivered?.given ?? 0), 0);
    cells.push({ label: 'Delivered this month', value: `${s.given} units`, tone: waiting ? 'text-queued' : undefined, open: ['calendar', 'calendar'],
      sub: waiting ? `+ ${waiting} delivered by the rider, not booked yet (no stock on the round)` : `${s.deliveredDays} day(s) · 1–${Number(cal.data.today.slice(8))} ${new Date(`${today}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' })}` });
  }
  if (f && !facts.error) {
    cells.push({ label: 'Bottles with customer', value: `${f.bottlesOwed ?? 0} bottles`, sub: f.bottlesOwed ? `oldest ${f.oldestOwedDays ?? '—'} d out` : 'none owed', open: ['deliveries', 'bottles'] });
    // Only a real start date: Fleet v1 and the workbook carry none, and the ERP does not guess one.
    if (f.since) cells.push({ label: 'Member since', value: new Date(`${f.since.slice(0, 10)}T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' }), sub: months(f.since) });
  }
  if (complaints.data && !complaints.error) {
    const open = complaints.data.filter((c) => c.status === 'RECEIVED').length;
    const last = complaints.data.map((c) => c.decided_at).filter(Boolean).sort().pop();
    cells.push({ label: 'Open tickets', value: `${open} ticket${open === 1 ? '' : 's'}`, tone: open > 0 ? 'text-queued' : undefined, sub: last ? `last decided ${shortDay(last)}` : 'none decided yet', open: ['service', 'tickets'] });
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-small text-ink-muted">
        {entity.phone ? <span className="inline-flex items-center gap-1 code-text"><Phone size={12} aria-hidden />{entity.phone}{!entity.may_see_personal_data ? <span className="ml-1 font-sans text-ink-faint">masked</span> : null}</span> : null}
        {door?.area ? <span className="inline-flex items-center gap-1"><MapPin size={12} aria-hidden />{door.area}</span> : null}
        <span className="inline-flex items-center gap-1 code-text"><RouteIcon size={12} aria-hidden />{entity.route?.code ? `Route ${entity.route.code}` : 'Not on a round'}</span>
        {f?.route?.usualRiderCode || f?.route?.usualRider ? <span className="inline-flex items-center gap-1"><User size={12} aria-hidden />Rider {f.route.usualRiderCode ?? ''} {f.route.usualRider ?? ''}</span> : null}
      </div>
      {cells.length > 0 ? (
        <div className={cx('mt-3 grid grid-cols-2 border-t border-line sm:grid-cols-3', cells.length >= 5 ? 'lg:grid-cols-5' : cells.length === 4 ? 'lg:grid-cols-4' : '')}>
          {cells.map((c) => (
            <div key={c.label} className="border-line first:pl-0 sm:border-l sm:first:border-l-0">
              <button type="button" disabled={!c.open} onClick={() => c.open && openTab(c.open[0], c.open[1])}
                title={c.open ? 'Open the details' : undefined}
                className={cx('block w-full px-3 py-2.5 text-left', c.open && 'rounded-[var(--radius-control)] hover:bg-surface-sunken')}>
                <span className="label-text block">{c.label}</span>
                <span className={cx('mt-0.5 block text-lg font-semibold tabular-nums', c.tone, c.open && 'underline decoration-dotted underline-offset-4')}>{c.value}</span>
                {c.sub ? <span className="block truncate text-small text-ink-muted">{c.sub}</span> : null}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ── The scheduled-change banner (UI_KIT pattern 06) ───────────────────────────────────────────────── */

export function ScheduledChangeBanner({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const today = istToday();
  const may = can('subscription.view');
  const a = useCalendarMonth(entity.id, monthStart(today), may);
  const b = useCalendarMonth(entity.id, nextMonth(today), may);
  const openTab = useOpenTab();
  const days = [...(a.data?.days ?? []), ...(b.data?.days ?? [])].filter((d) => d.date > today);
  const seen = new Map<string, { text: string; from: string }>();
  for (const d of days) {
    for (const c of d.changes) {
      const key = `${c.effect}|${c.variant}|${c.from}|${c.until ?? ''}`;
      if (!seen.has(key)) {
        const span = c.until && c.until !== c.from ? `${shortDay(c.from)} – ${shortDay(c.until)}` : c.until ? shortDay(c.from) : `from ${shortDay(c.from)}`;
        seen.set(key, { from: c.from, text: `${c.effect.toLowerCase().replace(/_/g, ' ')} ${c.variant}${c.quantity !== null ? ` × ${c.quantity}` : ''} · ${span}${c.reason ? ` — ${c.reason}` : ''}` });
      }
    }
    if (d.extraOrders > 0) seen.set(`extra|${d.date}`, { from: d.date, text: `an extra order · ${shortDay(d.date)}` });
  }
  const list = [...seen.values()].sort((x, y) => x.from.localeCompare(y.from));
  if (list.length === 0) return null;
  return (
    <div role="status" className="flex flex-wrap items-center gap-3 border-b border-line bg-info-ground px-6 py-2 text-small">
      <CalendarClock size={14} className="shrink-0 text-info" aria-hidden />
      <span className="min-w-0 flex-1"><span className="font-medium text-ink">Scheduled: </span>{list[0]!.text}{list.length > 1 ? <span className="text-ink-muted"> · and {list.length - 1} more in the next two months</span> : null}</span>
      <button type="button" className="font-medium text-brand hover:underline" onClick={() => openTab('subscription', 'calendar')}>See in calendar</button>
    </div>
  );
}

/* ── Overview › At a glance: the board's cards, each with one link to its tab ──────────────────────── */

function DayCard({ label, day, emphasise, phone }: { label: string; day: CalendarDay | undefined; emphasise?: boolean; phone?: PhoneDay }) {
  const lines = day ? dayLines(day) : [];
  const waiting = waitingUnits(phone, day?.delivered?.given ?? 0);
  return (
    <div className={cx('rounded-[var(--radius-control)] border px-3 py-2.5', emphasise ? 'border-brand' : 'border-line')}>
      <div className="flex items-center justify-between gap-2">
        <span className="label-text">{label} · {day ? shortDay(day.date) : '—'}</span>
        {day?.delivered ? (
          <StatusBadge tone={day.delivered.outcome === 'DELIVERED' ? 'accepted' : day.delivered.outcome === 'NOT_DELIVERED' ? 'refused' : 'queued'} icon={day.delivered.outcome === 'DELIVERED' ? CircleCheck : CircleAlert}>{day.delivered.outcome.toLowerCase().replace(/_/g, ' ')}</StatusBadge>
        ) : day?.order ? <StatusBadge tone="info" icon={Truck}>{day.order.status.toLowerCase()}</StatusBadge> : day && lines.length ? <StatusBadge tone="info" icon={CalendarCheck}>planned</StatusBadge> : null}
      </div>
      <div className="mt-1 text-lg font-semibold">{day ? (day.delivered ? `${day.delivered.given} of ${day.delivered.ordered} given` : linesLabel(lines)) : <Unknown title="Not read" />}</div>
      {waiting > 0 ? <div className="text-small font-medium text-queued" title={phone?.heldDetail ?? undefined}>{waiting} delivered by the rider · not booked yet ({heldShort(phone?.heldCode ?? null)})</div> : null}
      <div className="mt-0.5 text-small text-ink-muted">
        {day?.order ? <span className="code-text">{day.order.code}</span> : day ? 'from the agreement' : ''}
        {day && day.changes.length > 0 ? ` · ${day.changes.map((c) => c.effect.toLowerCase()).join(', ')}` : ''}
        {day && day.extraOrders > 0 ? ' · an extra order' : ''}
        {day?.locked ? ' · confirmed at the cutoff' : ''}
      </div>
    </div>
  );
}

export function CustomerOverview({ entity }: { entity: Customer360 }) {
  const business = useBusinessDay();
  const { can } = useSession();
  const openTab = useOpenTab();
  const today = istToday();
  const tomorrow = addDays(today, 1);
  const twoDays = useCalendarRange(entity.id, can('subscription.view') ? today : null, tomorrow);
  const balance = useCustomerBalance(entity.id);
  const bill = useMonthBill(entity.id, today.slice(0, 7));
  const facts = useDeliveryFacts(entity.id);
  const timeline = useCustomerTimeline(entity.id);
  const door = entity.locations.find((l) => l.is_primary) ?? entity.locations[0];
  const byDate = new Map((twoDays.data?.days ?? []).map((d) => [d.date, d]));
  const phoneDays = usePhoneDays(entity.id);
  const subs = entity.subscriptions ?? [];
  const taken = entity.products.filter((p) => p.takes || p.assigned);
  // The office's own note on this customer (the Note on its master record), pinned as a scrapbook note.
  const officeNote = useQuery({ queryKey: ['customer-office-note', entity.id], queryFn: async () => {
    const { data, error } = await supabase.from('customers').select('note').eq('id', entity.id).maybeSingle();
    if (error) throw error;
    return (data?.note as string | null | undefined) ?? null;
  } });

  // Two columns only when both fit (the owner's CRM brief, 2026-10-18): this card sits in the record page's middle
  // column, so the window's width says nothing about its own; a breakpoint here drew the cards over each other.
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))]">
      <div className="grid min-w-0 content-start gap-4">
        {officeNote.data ? <div className="pt-2"><ScrapNote title="Office note" hold="pin" tilt={-1.5} className="max-w-xl whitespace-pre-line">{officeNote.data}</ScrapNote></div> : null}
        <OverviewCard title="Today & tomorrow" note={`Tomorrow changes until the cut-off (${cutoffWords(business.data)})`} link={{ label: 'Subscription & calendar', onClick: () => openTab('subscription', 'calendar') }}>
          {twoDays.error || !can('subscription.view') ? (
            <p className="text-small text-ink-muted">The calendar is shown to whoever may see subscriptions.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <DayCard label="Today" day={byDate.get(today)} phone={phoneDays.data?.get(today)} />
              <DayCard label="Tomorrow" day={byDate.get(tomorrow)} emphasise />
            </div>
          )}
        </OverviewCard>

        <OverviewCard title="Standing arrangement" note={subs[0] ? `since ${shortDay(subs[subs.length - 1]!.start_date)}` : undefined}
          link={{ label: 'Open the Agreement', onClick: () => openTab('subscription', 'agreement') }}>
          {entity.subscriptions === null ? <p className="text-small text-ink-muted">Standing agreements are shown to whoever may see subscriptions.</p>
            : subs.length === 0 ? <p className="text-small text-ink-muted">This customer has no standing agreement.</p> : (
              <ul className="grid gap-1 text-body">
                {subs.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-x-3">
                    <span className="code-text">{s.code}</span>
                    <span>{s.units_per_delivery} unit(s) · {s.schedule_pattern.toLowerCase()}</span>
                    <span className="text-small text-ink-muted">{s.status.toLowerCase()}{s.paused_from ? ` · paused from ${shortDay(s.paused_from)}` : ''}{s.end_date ? ` · until ${shortDay(s.end_date)}` : ''}</span>
                  </li>))}
              </ul>)}
          {taken.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-3 border-t border-line pt-2">
              {taken.map((p) => <span key={p.variant_code} className="inline-flex items-center gap-1 text-small text-ink-muted"><VariantBottle code={p.variant_code} size={22} />{p.variant_name}</span>)}
            </div>) : null}
          <p className="mt-2 text-small text-ink-muted">Price list: <span className="code-text">{entity.price_list_code ?? '—'}</span></p>
          {/* 127600: the days delivered, the dated changes waiting, and changing items or days from a date. */}
          {entity.subscriptions !== null ? <ArrangementManage entity={entity} /> : null}
          <PlannedPanel className="mt-2" title="Rate that will bill and the month's estimate" step="v6 S5 — billing and collections"
            items={['The rate each line bills at, and where it comes from', 'This month at the current plan: units and amount']}
            builtSoFar="what each month actually charged is in Bills & payments › Outstanding." />
        </OverviewCard>

        <OverviewCard title="Delivery point" note={door ? (door.has_geo_pin ? 'map pin set' : 'no map pin yet') : undefined}
          link={{ label: 'All doors', onClick: () => openTab('overview', 'locations') }}>
          {door ? (
            <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-body">
              <dt className="text-ink-muted">Door</dt><dd className="code-text">{door.code}{door.label ? <span className="ml-2 font-sans">{door.label}</span> : null}</dd>
              <dt className="text-ink-muted">Address</dt><dd>{door.address_line ?? (entity.may_see_personal_data ? <Unknown title="No street on record" /> : 'hidden')}{door.area ? ` · ${door.area}` : ''}</dd>
              <dt className="text-ink-muted">Pin</dt><dd>{facts.data?.pin ? (facts.data.pin.status === 'ok' ? `set${facts.data.pin.source ? ` · ${facts.data.pin.source.toLowerCase().replace(/_/g, ' ')}` : ''}` : 'none yet — the rider pins it at the gate') + (facts.data.pin.pendingApproval ? ' · a new pin waits for approval' : '') : door.has_geo_pin ? 'set' : 'none'}</dd>
              <dt className="text-ink-muted">Round</dt><dd className="code-text">{facts.data?.route?.code ?? entity.route?.code ?? '—'}</dd>
            </dl>
          ) : <p className="text-small text-ink-muted">This customer has no door on record.</p>}
          <div className="mt-2"><MoveDoorButton entity={entity} /></div>
        </OverviewCard>
      </div>

      <div className="grid min-w-0 content-start gap-4">
        <OverviewCard title="Account summary" link={{ label: 'Bills & payments', onClick: () => openTab('bills', 'outstanding') }}>
          {balance.error ? <p className="text-small text-ink-muted">Money is shown to whoever may see it.</p> : (
            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-body">
              <dt className="text-ink-muted">Outstanding</dt><dd className={cx('text-right font-semibold tabular-nums', (balance.data?.balance_paise ?? 0) > 0 && 'text-refused')}>{inr(balance.data?.balance_paise)}</dd>
              {balance.data ? <dd className="col-span-2 -mt-1 text-small text-ink-muted">{outstandingSplit(balance.data)} · {balanceSourceLine(balance.data.source)} · <Link className="text-info underline" to={ledgerCheckPath(entity.code)}>Open in Finance</Link></dd> : null}
              {bill.data ? <><dt className="text-ink-muted">Charged this month so far</dt><dd className="text-right tabular-nums">{inr(bill.data.charged)}</dd></> : null}
              <dt className="text-ink-muted">Last payment</dt><dd className="text-right tabular-nums">{balance.data?.last_paid_on ? shortDay(balance.data.last_paid_on) : 'never'}</dd>
              <dt className="text-ink-muted">Paid, all time</dt><dd className="text-right tabular-nums">{inr(balance.data?.paid_paise)}</dd>
            </dl>)}
          <PlannedPanel className="mt-3" title="Next bill" step="v6 S5 — billing and collections" items={['The next bill run date and the channel it goes by']} />
        </OverviewCard>

        <OverviewCard title="Recent activity" note="All apps" link={{ label: 'History', onClick: () => openTab('history', 'activity') }}>
          {timeline.error ? <p className="text-small text-ink-muted">{(timeline.error as Error).message}</p>
            : (timeline.data ?? []).length === 0 ? <p className="text-small text-ink-muted">{timeline.isLoading ? 'Loading…' : 'Nothing yet.'}</p> : (
              <ol className="grid gap-2">
                {(timeline.data ?? []).slice(0, 6).map((e, i) => (
                  <li key={i} className="grid grid-cols-[0.5rem_1fr] gap-2">
                    <span aria-hidden className="mt-1.5 h-2 w-2 rounded-full bg-brand" />
                    <div className="min-w-0">
                      <p className="truncate text-body font-medium">{e.what}</p>
                      <p className="truncate text-small text-ink-muted">{e.at ? fmtDateTime(e.at) : 'undated'} · {e.kind.toLowerCase()}{e.detail ? ` · ${e.detail}` : ''}</p>
                    </div>
                  </li>))}
              </ol>)}
        </OverviewCard>
      </div>
    </div>
  );
}

/* ── Subscription & calendar › Calendar: the month, with "this month so far" and "next up" beside it ─ */

export function SubscriptionCalendar({ entity }: { entity: Customer360 }) {
  const today = istToday();
  const cal = useCalendarMonth(entity.id, monthStart(today));
  const ahead = useCalendarMonth(entity.id, nextMonth(today));
  const s = cal.data ? monthSoFar(cal.data.days, cal.data.today) : null;
  const next = [...(cal.data?.days ?? []), ...(ahead.data?.days ?? [])].find((d) => d.date > today && unitsOf(dayLines(d)) > 0);
  const scheduled = [...(cal.data?.days ?? []), ...(ahead.data?.days ?? [])].some((d) => d.date > today && (d.changes.length > 0 || d.extraOrders > 0));
  const monthName = new Date(`${today}T00:00:00Z`).toLocaleDateString(undefined, { month: 'long', timeZone: 'UTC' });
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(min(100%,20rem),1fr))]">
      <div className="min-w-0"><CustomerCalendar entity={entity} /></div>
      <div className="grid content-start gap-4">
        <OverviewCard title={`${monthName} so far`}>
          {s ? (
            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1.5 text-body">
              <dt className="text-ink-muted">Delivered</dt><dd className="text-right tabular-nums">{s.given} units · {s.deliveredDays} days</dd>
              <dt className="text-ink-muted">Changed or extra</dt><dd className="text-right tabular-nums">{s.changed} days</dd>
              <dt className="text-ink-muted">Part delivered</dt><dd className="text-right tabular-nums">{s.partial} days</dd>
              <dt className="text-ink-muted">Not delivered</dt><dd className="text-right tabular-nums">{s.notDelivered} days</dd>
              <dt className="text-ink-muted">Still to come</dt><dd className="text-right tabular-nums">{s.comingPlanned} days</dd>
            </dl>) : <p className="text-small text-ink-muted">{cal.error ? (cal.error as Error).message : 'Loading…'}</p>}
          <PlannedPanel className="mt-3" title="Month-end estimate" step="v6 S5 — billing and collections" items={['Units and amount at the current plan and rate']} />
        </OverviewCard>
        <OverviewCard title="Next up">
          <p className="text-body font-medium">{next ? `${shortDay(next.date)} · ${linesLabel(dayLines(next))}` : 'Nothing planned in the next two months.'}</p>
          <p className="mt-0.5 text-small text-ink-muted">{scheduled ? 'A change is scheduled: see the banner above and the amber days.' : 'No change, pause or extra order scheduled.'}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <ChangeDeliveryButton />
            <ChangeDeliveryButton type="skip" label="Skip a date" variant="secondary" />
          </div>
        </OverviewCard>
      </div>
    </div>
  );
}
